import fromUnixTime from 'date-fns/fromUnixTime';
import Cookies from 'js-cookie';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { SESSION_STORAGE_KEYS } from 'dashboard/constants/sessionStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import SessionStorage from 'shared/helpers/sessionStorage';
import { emitter } from 'shared/helpers/mitt';
import { broadcastAuthSignal } from 'dashboard/helper/authSignal';
import {
  ANALYTICS_IDENTITY,
  ANALYTICS_RESET,
  CHATWOOT_RESET,
  CHATWOOT_SET_USER,
} from '../../constants/appEvents';

Cookies.defaults = { sameSite: 'Lax' };

export const AUTH_COOKIE_NAME = 'cw_d_session_info';
export const AUTH_HEADER_KEYS = [
  'access-token',
  'token-type',
  'client',
  'expiry',
  'uid',
];

export const getLoadingStatus = state => state.fetchAPIloadingStatus;
export const setLoadingStatus = (state, status) => {
  state.fetchAPIloadingStatus = status;
};

export const setUser = user => {
  emitter.emit(CHATWOOT_SET_USER, { user });
  emitter.emit(ANALYTICS_IDENTITY, { user });
};

const readAuthHeader = (headers, key) => {
  if (!headers) return undefined;
  if (typeof headers.get === 'function') {
    return headers.get(key) || undefined;
  }
  return headers[key];
};

export const extractAuthHeaders = headers =>
  AUTH_HEADER_KEYS.reduce((result, key) => {
    const value = readAuthHeader(headers, key);
    if (value !== undefined && value !== null && value !== '') {
      result[key] = value;
    }
    return result;
  }, {});

export const hasRequiredAuthHeaders = headers =>
  ['access-token', 'token-type', 'client', 'uid'].every(key =>
    Boolean(headers?.[key])
  );

export const getHeaderExpiry = response => {
  const authHeaders = extractAuthHeaders(response?.headers);
  const expiry = authHeaders.expiry;
  return expiry ? fromUnixTime(Number(expiry)) : null;
};

export const storeAuthHeaders = authHeaders => {
  if (!hasRequiredAuthHeaders(authHeaders)) {
    return false;
  }

  const cookieOptions = {};
  const expiry = authHeaders.expiry
    ? fromUnixTime(Number(authHeaders.expiry))
    : null;
  if (expiry && !Number.isNaN(expiry.getTime())) {
    cookieOptions.expires = expiry;
  }

  Cookies.set(AUTH_COOKIE_NAME, JSON.stringify(authHeaders), cookieOptions);
  return true;
};

export const setAuthCredentials = response => {
  const expiryDate = getHeaderExpiry(response);
  const authHeaders = extractAuthHeaders(response?.headers);
  storeAuthHeaders(authHeaders);
  setUser(response.data.data, expiryDate);
  // Open dashboard tabs of another account check the cookie and offer a refresh.
  broadcastAuthSignal({ type: 'login', uid: authHeaders.uid });
};

export const clearBrowserSessionCookies = () => {
  Cookies.remove(AUTH_COOKIE_NAME);
  Cookies.remove('auth_data');
  Cookies.remove('user');
};

export const clearLocalStorageOnLogout = () => {
  LocalStorage.remove(LOCAL_STORAGE_KEYS.DRAFT_MESSAGES);
};

export const clearSessionStorageOnLogout = () => {
  SessionStorage.remove(SESSION_STORAGE_KEYS.IMPERSONATION_USER);
};

export const deleteIndexedDBOnLogout = async () => {
  let dbs = [];
  try {
    dbs = await window.indexedDB.databases();
    dbs = dbs.map(db => db.name);
  } catch (e) {
    dbs = JSON.parse(localStorage.getItem('cw-idb-names') || '[]');
  }

  dbs.forEach(dbName => {
    const deleteRequest = window.indexedDB.deleteDatabase(dbName);

    deleteRequest.onerror = event => {
      // eslint-disable-next-line no-console
      console.error(`Error deleting database ${dbName}.`, event);
    };

    deleteRequest.onsuccess = () => {
      // eslint-disable-next-line no-console
      console.log(`Database ${dbName} deleted successfully.`);
    };
  });

  localStorage.removeItem('cw-idb-names');
};

const authCookieUid = () => {
  try {
    return JSON.parse(Cookies.get(AUTH_COOKIE_NAME) || '{}').uid || null;
  } catch (e) {
    return null;
  }
};

// ownUid: the account this tab signed in as. If another tab has since signed in as a
// different account, the shared cookie and drafts are that account's: leave them.
export const clearCookiesOnLogout = (
  redirectTo = null,
  { ownUid = null } = {}
) => {
  emitter.emit(CHATWOOT_RESET);
  emitter.emit(ANALYTICS_RESET);
  const cookieUid = authCookieUid();
  if (!ownUid || !cookieUid || cookieUid === ownUid) {
    clearBrowserSessionCookies();
    clearLocalStorageOnLogout();
  }
  clearSessionStorageOnLogout();
  const globalConfig = window.globalConfig || {};
  const logoutRedirectLink =
    redirectTo || globalConfig.LOGOUT_REDIRECT_LINK || '/';
  window.location = logoutRedirectLink;
};

export const parseAPIErrorResponse = error => {
  if (error?.response?.data?.message) {
    return error?.response?.data?.message;
  }
  if (error?.response?.data?.error) {
    return error?.response?.data?.error;
  }
  if (error?.response?.data?.errors) {
    return error?.response?.data?.errors[0];
  }
  return error;
};

export const throwErrorMessage = error => {
  const errorMessage = parseAPIErrorResponse(error);
  throw new Error(errorMessage);
};

export const parseLinearAPIErrorResponse = (error, defaultMessage) => {
  const errorData = error.response.data;
  const errorMessage = errorData?.error?.errors?.[0]?.message || defaultMessage;
  return errorMessage;
};
